import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ReferenceAdapter } from '../reference.adapter';
import { ValueAdapter } from './../value.adapter';
import _ from 'lodash';
import { ExecuteDataFlowActivity } from 'app/model/pipeline/execute-dataflow.activity';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { LinkedService } from '../../linkedservice/linkedservice';
import { linkedServiceReferenceToResource } from '../../../shared/utils/linkedservice-parameter.util';
import { convertParametersToModel, fillParameterValuesForResource, unwrapDataFlowParams, wrapDataFlowPipelineExpression } from '../../../shared/utils/parameter-converter.util';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ParameterValue } from 'app/model/base/parameter-value';
import { Dataset } from '../../dataset/dataset';
import { irDefaultInstanceName } from 'app/integrationruntime/integrationruntime.constants';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExecuteDataFlowActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            dataflow: '*',
            staging: {
                linkedService: '*',
                folderPath: '*'
            },
            compute: '*',
            traceLevel: '*',
            runConcurrently: '*',
            sourceStagingConcurrency: '*',
            continueOnError: '*',
            allowCaching: '*',
            cacheSinks: {
                firstRowOnly: '*'
            },
            integrationRuntime: '*',
            continuationSettings: {
                customizedCheckpointKey: '*'
            },
            continuationMode: '*'
        }
    }
})
export class ExecuteDataFlowActivityAdapter extends ActivityAsyncAdapter<ExecuteDataFlowActivity, Model.ExecuteDataFlowActivity> {
    public model(): IModel {
        return Metamodel.modelFor(ExecuteDataFlowActivity);
    }

    protected async _toResource(activity: ExecuteDataFlowActivity): Promise<Model.ExecuteDataFlowActivity> {
        const modelDataFlowActivity: Model.ExecuteDataFlowActivity = super._getCommonActivityProperties(activity, ExecuteDataFlowActivity.MODEL.name) as Model.ExecuteDataFlowActivity;
        modelDataFlowActivity.typeProperties = {
            dataflow: activity.dataflow ? this._toDataFlowReferenceModel(activity) : undefined,
            staging: {
                linkedService: activity.stagingLinkedService
                    ? linkedServiceReferenceToResource(activity.stagingLinkedService, this.context,
                        this.ensureLogger(), activity.stagingLinkedServiceParameters) : undefined,
                folderPath: ValueAdapter.toResource(activity, ExecuteDataFlowActivity.MODEL.attribute('stagingStoragePath'))
            }
        };
        if (activity.integrationRuntime && activity.integrationRuntime.name !== irDefaultInstanceName) {
            modelDataFlowActivity.typeProperties.integrationRuntime = ReferenceAdapter.toResource(activity.integrationRuntime, this.context) as Model.IntegrationRuntimeReference;
            modelDataFlowActivity.typeProperties.compute = undefined;
        } else {
            modelDataFlowActivity.typeProperties.compute = {
                coreCount: ValueAdapter.toResource(activity, ExecuteDataFlowActivity.CORE_COUNT),
                computeType: ValueAdapter.toResource(activity, ExecuteDataFlowActivity.COMPUTE_TYPE)
            };
        }
        modelDataFlowActivity.typeProperties.traceLevel = ValueAdapter.toResource(activity, ExecuteDataFlowActivity.TRACE_LEVEL);
        modelDataFlowActivity.typeProperties.runConcurrently = ValueAdapter.toResource(activity, ExecuteDataFlowActivity.RUN_CONCURRENTLY);
        if (activity.dataflow && activity.dataflow.useSourceStagingConcurrencySettings()) {
            modelDataFlowActivity.typeProperties.sourceStagingConcurrency = ValueAdapter.toResource(activity, ExecuteDataFlowActivity.SOURCE_STAGING_CONCURRENCY);
        }
        modelDataFlowActivity.typeProperties.continueOnError = ValueAdapter.toResource(activity, ExecuteDataFlowActivity.CONTINUE_ON_ERROR);
        modelDataFlowActivity.typeProperties.allowCaching = ValueAdapter.toResource(activity, ExecuteDataFlowActivity.ALLOW_CACHING);
        modelDataFlowActivity.typeProperties.cacheSinks = modelDataFlowActivity.typeProperties.traceLevel === 'None' ? {
            firstRowOnly: ValueAdapter.toResource(activity, ExecuteDataFlowActivity.FIRST_ROW_ONLY)
        } : undefined;
        modelDataFlowActivity.typeProperties.dataFlowDebugSessionId = activity.dataFlowDebugSessionId;
        if (activity.dataflow) {
            const customizedKey = ValueAdapter.toResource(activity, ExecuteDataFlowActivity.CUSTOMIZED_CHECKPOINT_KEY);
            if (!stringIsNullOrEmpty(customizedKey)) {
                modelDataFlowActivity.typeProperties.continuationSettings = {
                    customizedCheckpointKey: customizedKey
                };
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelDataFlowActivity, parsed);
        }
        return modelDataFlowActivity;
    }

    protected async _toModel(modelActivity: Model.ExecuteDataFlowActivity): Promise<ExecuteDataFlowActivity> {
        const activity: ExecuteDataFlowActivity = ExecuteDataFlowActivity.MODEL.create<ExecuteDataFlowActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);

        // Temporal workaround for SDK serialization issue (it serializes to dataFlow instead of dataflow). Bug #558995
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aModelActivity = modelActivity as any;
        if (aModelActivity.typeProperties && aModelActivity.typeProperties.dataFlow && !modelActivity.typeProperties.dataflow) {
            modelActivity.typeProperties.dataflow = aModelActivity.typeProperties.dataFlow;
            delete aModelActivity.typeProperties.dataFlow;
        }

        if (modelActivity.typeProperties && modelActivity.typeProperties.dataflow) {
            activity.dataflow = ReferenceAdapter.toProxyModel<DataFlow>(Metamodel.modelFor(DataFlow), modelActivity.typeProperties.dataflow, this.context);
            if (modelActivity.typeProperties.dataflow.parameters) {
                convertParametersToModel(activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    activity.dataflow as any,
                    activity.dataflowParameters,
                    modelActivity.typeProperties.dataflow.parameters);
                unwrapDataFlowParams(activity.dataflowParameters);
            }
            if (modelActivity.typeProperties.dataflow.datasetParameters) {
                Object.keys(modelActivity.typeProperties.dataflow.datasetParameters).forEach((key) => {
                    if (modelActivity.typeProperties.dataflow.datasetParameters[key]) {
                        activity.dataflowDatasetParameters.set(key, []);
                        // parameters will be created with identity as if they belong to dataflow (instead of the dataset), because we don't have the reference
                        // to the dataset yet, but it will be replaced with the right object later on in parameters accordion component.
                        convertParametersToModel(activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            activity.dataflow as any,
                            activity.dataflowDatasetParameters.get(key),
                            modelActivity.typeProperties.dataflow.datasetParameters[key]);
                    }
                });
            }
            if (modelActivity.typeProperties.dataflow.linkedServiceParameters) {
                Object.keys(modelActivity.typeProperties.dataflow.linkedServiceParameters).forEach((key) => {
                    if (modelActivity.typeProperties.dataflow.linkedServiceParameters[key]) {
                        activity.dataflowLinkedServiceParameters.set(key, []);
                        convertParametersToModel(activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            activity.dataflow as any,
                            activity.dataflowLinkedServiceParameters.get(key),
                            modelActivity.typeProperties.dataflow.linkedServiceParameters[key].linkedService);
                        activity.schemaLinkedServiceParameters.set(key, []);
                        convertParametersToModel(activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            activity.dataflow as any,
                            activity.schemaLinkedServiceParameters.get(key),
                            modelActivity.typeProperties.dataflow.linkedServiceParameters[key].schemaLinkedService);
                        activity.rejectedDataLinkedServiceParameters.set(key, []);
                        convertParametersToModel(activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            activity.dataflow as any,
                            activity.rejectedDataLinkedServiceParameters.get(key),
                            modelActivity.typeProperties.dataflow.linkedServiceParameters[key].rejectedDataLinkedService);
                    }
                });
            }
        }

        if (modelActivity.typeProperties && modelActivity.typeProperties.staging && modelActivity.typeProperties.staging.linkedService) {
            activity.stagingLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(LinkedService), modelActivity.typeProperties.staging.linkedService, this.context);
            ValueAdapter.toModel(activity, ExecuteDataFlowActivity.MODEL.attribute('stagingStoragePath'), modelActivity.typeProperties.staging.folderPath);
            if (modelActivity.typeProperties.staging.linkedService.parameters) {
                // Parameter value for linked service
                convertParametersToModel(activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.stagingLinkedService,
                    activity.stagingLinkedServiceParameters,
                    modelActivity.typeProperties.staging.linkedService.parameters);
            }
        }

        if (modelActivity.typeProperties && modelActivity.typeProperties.integrationRuntime) {
            activity.integrationRuntime = ReferenceAdapter.toProxyModel<IntegrationRuntime>(Metamodel.modelFor(IntegrationRuntime), modelActivity.typeProperties.integrationRuntime, this.context);
        } else if (modelActivity.typeProperties.compute) {
            ValueAdapter.toModel(activity, ExecuteDataFlowActivity.CORE_COUNT, modelActivity.typeProperties.compute.coreCount);
            ValueAdapter.toModel(activity, ExecuteDataFlowActivity.COMPUTE_TYPE, modelActivity.typeProperties.compute.computeType);
        }

        if (modelActivity.typeProperties) {
            if (modelActivity.typeProperties.traceLevel) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.TRACE_LEVEL, modelActivity.typeProperties.traceLevel);
            }
            if (modelActivity.typeProperties.runConcurrently) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.RUN_CONCURRENTLY, modelActivity.typeProperties.runConcurrently);
            }
            if (modelActivity.typeProperties.sourceStagingConcurrency) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.SOURCE_STAGING_CONCURRENCY, modelActivity.typeProperties.sourceStagingConcurrency);
            }
            if (modelActivity.typeProperties.continueOnError) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.CONTINUE_ON_ERROR, modelActivity.typeProperties.continueOnError);
            }
            if (modelActivity.typeProperties.allowCaching) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.ALLOW_CACHING, modelActivity.typeProperties.allowCaching);
            }
            if (modelActivity.typeProperties.cacheSinks && modelActivity.typeProperties.cacheSinks.firstRowOnly) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.FIRST_ROW_ONLY, modelActivity.typeProperties.cacheSinks.firstRowOnly);
            }
            if (modelActivity.typeProperties.continuationSettings && modelActivity.typeProperties.continuationSettings.customizedCheckpointKey) {
                ValueAdapter.toModel(activity, ExecuteDataFlowActivity.CUSTOMIZED_CHECKPOINT_KEY, modelActivity.typeProperties.continuationSettings.customizedCheckpointKey);
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, ExecuteDataFlowActivity.MODEL));

        return activity;
    }

    private _toDataFlowReferenceModel(dataflowActivity: ExecuteDataFlowActivity): Model.DataFlowReference {
        const dataflowReference = ReferenceAdapter.toResource(dataflowActivity.dataflow, this.context) as Model.DataFlowReference;
        // TODO - ans - remove this once backend correct the casing for reference type.
        dataflowReference.type = 'DataFlowReference';
        const paramsCopy = wrapDataFlowPipelineExpression(dataflowActivity.dataflowParameters);
        dataflowReference.parameters = fillParameterValuesForResource(paramsCopy, this.context,
            this.ensureLogger());
        dataflowReference.datasetParameters = {};

        if (dataflowActivity.dataflowLinkedServiceParameters.size || dataflowActivity.schemaLinkedServiceParameters.size || dataflowActivity.rejectedDataLinkedServiceParameters.size) {
            dataflowReference.linkedServiceParameters = {};
        }

        if (dataflowActivity && dataflowActivity.dataflow) {
            dataflowActivity.dataflow.sources.forEach((source) => {
                const datasetParameters = dataflowActivity.dataflowDatasetParameters.get(source.name);
                this.resetDatasetParameter(datasetParameters, source.sourceDataset);
                dataflowReference.datasetParameters[source.name] = fillParameterValuesForResource(dataflowActivity.dataflowDatasetParameters.get(source.name), this.context,
                this.ensureLogger());
                if (dataflowActivity.dataflowLinkedServiceParameters.get(source.name)?.length
                    || dataflowActivity.schemaLinkedServiceParameters.get(source.name)?.length) {
                    this.resetDatasetParameter(dataflowActivity.dataflowLinkedServiceParameters.get(source.name), source.sourceLinkedService);
                    this.resetDatasetParameter(dataflowActivity.schemaLinkedServiceParameters.get(source.name), source.schemaLinkedService);
                    dataflowReference.linkedServiceParameters[source.name] = {};
                    if (dataflowActivity.dataflowLinkedServiceParameters.get(source.name)?.length) {
                        dataflowReference.linkedServiceParameters[source.name].linkedService
                            = fillParameterValuesForResource(dataflowActivity.dataflowLinkedServiceParameters.get(source.name), this.context,
                            this.ensureLogger());
                    }
                    if (dataflowActivity.schemaLinkedServiceParameters.get(source.name)?.length) {
                        dataflowReference.linkedServiceParameters[source.name].schemaLinkedService
                            = fillParameterValuesForResource(dataflowActivity.schemaLinkedServiceParameters.get(source.name), this.context,
                            this.ensureLogger());
                    }
                }
            });
            dataflowActivity.dataflow.getParameterizedCallTransformations().forEach((transformation) => {
                if (dataflowActivity.dataflowLinkedServiceParameters.get(transformation.name)?.length) {
                    this.resetDatasetParameter(dataflowActivity.dataflowLinkedServiceParameters.get(transformation.name), transformation.source.sourceLinkedService);
                    dataflowReference.linkedServiceParameters[transformation.name] = {};
                    if (dataflowActivity.dataflowLinkedServiceParameters.get(transformation.name)?.length) {
                        dataflowReference.linkedServiceParameters[transformation.name].linkedService
                            = fillParameterValuesForResource(dataflowActivity.dataflowLinkedServiceParameters.get(transformation.name), this.context,
                            this.ensureLogger());
                    }
                }
            });
            dataflowActivity.dataflow.sinks.forEach((sink) => {
                const datasetParameters = dataflowActivity.dataflowDatasetParameters.get(sink.name);
                this.resetDatasetParameter(datasetParameters, sink.sinkDataset);
                dataflowReference.datasetParameters[sink.name] = fillParameterValuesForResource(dataflowActivity.dataflowDatasetParameters.get(sink.name), this.context,
                this.ensureLogger());

                if (dataflowActivity.dataflowLinkedServiceParameters.get(sink.name)?.length
                    || dataflowActivity.schemaLinkedServiceParameters.get(sink.name)?.length
                    || dataflowActivity.rejectedDataLinkedServiceParameters.get(sink.name)?.length) {
                    this.resetDatasetParameter(dataflowActivity.dataflowLinkedServiceParameters.get(sink.name), sink.sinkLinkedService);
                    this.resetDatasetParameter(dataflowActivity.schemaLinkedServiceParameters.get(sink.name), sink.schemaLinkedService);
                    this.resetDatasetParameter(dataflowActivity.rejectedDataLinkedServiceParameters.get(sink.name), sink.faultToleranceLinkedService);
                    dataflowReference.linkedServiceParameters[sink.name] = {};
                    if (dataflowActivity.dataflowLinkedServiceParameters.get(sink.name)?.length) {
                        dataflowReference.linkedServiceParameters[sink.name].linkedService
                            = fillParameterValuesForResource(dataflowActivity.dataflowLinkedServiceParameters.get(sink.name), this.context,
                            this.ensureLogger());
                    }
                    if (dataflowActivity.schemaLinkedServiceParameters.get(sink.name)?.length) {
                        dataflowReference.linkedServiceParameters[sink.name].schemaLinkedService
                            = fillParameterValuesForResource(dataflowActivity.schemaLinkedServiceParameters.get(sink.name), this.context,
                            this.ensureLogger());
                    }
                    if (dataflowActivity.rejectedDataLinkedServiceParameters.get(sink.name)?.length) {
                        dataflowReference.linkedServiceParameters[sink.name].rejectedDataLinkedService
                            = fillParameterValuesForResource(dataflowActivity.rejectedDataLinkedServiceParameters.get(sink.name), this.context,
                            this.ensureLogger());
                    }
                }
            });
        }
        return dataflowReference;
    }

    // If parameter owner is dataflow, it's the placeholder we generated in toModel
    // Need to reset it to the real parameter of dataset
    private resetDatasetParameter(datasetParameters: ParameterValue[], dataset: Dataset | LinkedService): void {
        // The source or sink dataset may not have parameters.
        if (dataset && datasetParameters) {
            datasetParameters.forEach((parameterValue) => {
                if (parameterValue.parameter && parameterValue.parameter.owner && Metamodel.modelOf(parameterValue.parameter.owner) === Metamodel.modelFor(DataFlow)) {
                    parameterValue.parameter = dataset.parameters.find((parameter) => parameter.name === parameterValue.parameter.name);
                }
            });
        }
    }
}
